import { useEffect } from 'react';
import { AuthenticatorType, useRepositoryMutation } from '@mews-commander/data-access';
import { LocalizedParagraph, OverlayedLoader, FieldsContainer } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import { LocalizedText, lt, showErrorAlert, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Container, Link, Stack } from '@optimus-web/core';
import { updateCurrentUser, useCurrentUser } from '@mews-commander/core';
import { PinInputField, TwoFactorFormValues, useTwoFactorForm } from '../../components/TwoFactorForm';
import { use2FAEmailCode } from '../Configure2FA/use2FAEmailCode';
import { useConfigure2FAPanel } from './useConfigure2FAPanel';

interface Configure2FAPanelValidateCodeProps {
    type: AuthenticatorType;
    // Secret key is needed for authenticator app
    secretKey?: string | null;
    onClose: () => void;
    onSubmit: (recoveryPins: string[]) => void;
    onReturn: () => void;
}

const authenticatorTextKeys = {
    [AuthenticatorType.Google]: { title: TextKey.VerifyIdentity, description: TextKey.VerifyIdentityInstructions },
    [AuthenticatorType.Email]: {
        title: TextKey.TwoFactorAuthenticationVerifyEmailLabel,
        description: TextKey.TwoFactorAuthenticationVerifyEmailDescription,
    },
    [AuthenticatorType.None]: { title: TextKey.FatalErrorMessage, description: TextKey.FatalErrorMessage },
};

const formId = 'settings-account-activate-2fa';

export function Configure2FAPanelValidateCode({
    onClose,
    onSubmit: onSuccessfulSubmit,
    onReturn,
    type,
    secretKey,
}: Configure2FAPanelValidateCodeProps) {
    const { userPassword } = useConfigure2FAPanel();
    const { onSubmit } = use2FASubmitHandler({
        authenticator: type,
        userPassword,
        secretKey,
        onSuccessfulSubmit,
    });
    const { renderFormContent, isSubmitting } = useTwoFactorForm({ onSubmit, id: formId });
    const { requestEmailAuthenticatorActivation, reRequestEmailAuthenticatorActivation } = use2FAEmailCode();

    useEffect(() => {
        if (type === AuthenticatorType.Email) {
            requestEmailAuthenticatorActivation();
        }
    }, [requestEmailAuthenticatorActivation, type]);

    return (
        <FloatingPanel
            modal
            size="small"
            onClose={onClose}
            active
            title={<LocalizedText textKey={authenticatorTextKeys[type].title} />}
            footer={{
                primary: (
                    <Button size="medium" variant="primary" type="submit" form={formId}>
                        <LocalizedText textKey={TextKey.Verify} />
                    </Button>
                ),
                secondary: (
                    <Button onClick={onReturn} size="medium" type="button">
                        <LocalizedText textKey={TextKey.GoBack} />
                    </Button>
                ),
            }}
        >
            {renderFormContent(
                <Stack vertical spacing="300">
                    <Stack vertical spacing="100">
                        <LocalizedParagraph textKey={authenticatorTextKeys[type].description} />
                        {type === AuthenticatorType.Email && (
                            <Link
                                onClick={reRequestEmailAuthenticatorActivation}
                                variant="basic"
                                size="small"
                                standalone
                            >
                                <LocalizedText textKey={TextKey.ResendEmail} />
                            </Link>
                        )}
                    </Stack>
                    <Container>
                        <OverlayedLoader show={isSubmitting} />
                        <FieldsContainer>
                            <PinInputField size="large" autoFocus />
                        </FieldsContainer>
                    </Container>
                </Stack>
            )}
        </FloatingPanel>
    );
}

function use2FASubmitHandler({
    authenticator,
    userPassword,
    secretKey,
    onSuccessfulSubmit,
}: {
    onSuccessfulSubmit: (recoveryPins: string[]) => void;
    secretKey?: string | null;
    userPassword: string;
    authenticator: AuthenticatorType;
}) {
    const { authenticator: currentAuthenticator } = useCurrentUser();
    const { mutateAsync } = useRepositoryMutation('users', 'updateAuthenticator', {
        meta: {
            disableDefaultErrorHandler: true,
        },
    });

    const onSubmit = async ({ pin, password }: TwoFactorFormValues) => {
        // We remove spaces from the pin to ensure a better UX
        const cleanPin = pin.replace(/\s/g, '');
        try {
            const { recoveryPins } = await mutateAsync({
                authenticator,
                pin: cleanPin,
                password: password || userPassword,
                secretKey,
            });
            updateCurrentUser({ authenticator });
            onSuccessfulSubmit(recoveryPins ?? []);
            showSuccessAlert({
                description: lt(
                    currentAuthenticator === AuthenticatorType.None
                        ? 'TwoFactorAuthenticationConfigured'
                        : 'TwoFactorAuthenticationUpdated'
                ),
            });
        } catch (error) {
            showErrorAlert({ description: <>{error.message}</> });
        }
    };

    return { onSubmit };
}
